import { FC, useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { ImageAsset, Text } from '@/components/common'
import LinkButton from '@/components/common/buttons/LinkButton'
import { NewCol } from '@/components/common/layouts/NewCol'
import { NewRow } from '@/components/common/layouts/NewRow'
import { FontWeightEnum } from '@/constants/font-weight.enum'
import { GlobalColorEnum } from '@/constants/global-color.enum'
import { LAYOUT_UNITS, SCREEN_MEDIA_QUERY, SCREEN_SIZES } from '@/constants/legacy/constLayout'
import ROUTES from '@/constants/legacy/constRoutes'
import { useCategoryTracker } from '@/containers/event-tracker/category.event-tracker'
import useCategoryContainerHook from '@/containers/hooks/category.container.hook'
import { AdPetType, CategoryType } from '@/graphql/generated/schemas'
import { enableScroll } from '@/utils/utilCommon'

type ListCategoriesSliderProps = {
  isAll: boolean
  rootCategory?: CategoryType
  subCategory?: CategoryType
  petType: AdPetType
}

type RenderItemColProps = {
  all?: boolean
  slug?: string
  name?: string
  petType?: AdPetType
  parentCategory?: CategoryType
  selected: boolean
  applySpan: boolean
  onClick: () => void
  scrollCenter?: (offset: number, width: number) => void
  width?: string
  isRootCategory?: boolean
}

const ListCategoriesSliderV2: FC<ListCategoriesSliderProps> = ({ isAll, rootCategory, subCategory, petType }) => {
  const { trackClickCategoryListItemEvent, trackClickCategoryListTabEvent } = useCategoryTracker()
  const scrollDivRef = useRef<HTMLDivElement | null>()
  const [showAll, toggleShowAll] = useState<boolean>(false)

  const rootCategoryId = rootCategory?.id
  const subCategoryId = subCategory?.id

  const { rootCategories, subCategories } = useCategoryContainerHook({
    petType,
    rootCategorySlug: rootCategoryId,
  })

  const subCategoriesWithAll = [
    {
      id: rootCategoryId,
      name: '전체',
      slug: 'all',
    },
    ...subCategories,
  ]

  useEffect(() => {
    return () => {
      enableScroll(true)
    }
  }, [rootCategoryId, subCategoryId])

  const handleClickCategory = ({
    toggle,
    rootCategoryId,
    subCategoryId,
  }: {
    toggle: boolean
    rootCategoryId?: string
    subCategoryId?: string
  }) => {
    toggleShowAll(toggle)
    enableScroll(!toggle)

    /**
     * ClickCategoryListTab tracker event
     */
    if (!rootCategoryId) {
      return
    }
    const rootCategoryName = rootCategories.find((c) => c.id === rootCategoryId)?.name
    const subCategoryName = subCategories.find((c) => c?.id === subCategoryId)?.name
    if (!rootCategoryName) {
      return
    }

    if (rootCategory?.id !== rootCategoryId) {
      trackClickCategoryListTabEvent({
        categoryTypeName: rootCategoryName,
        categoryName: subCategoryName || '전체',
      })
      return
    }

    trackClickCategoryListItemEvent({
      categoryTypeName: rootCategoryName,
      /**
       * ~가 전달되면, 트래커가 작동하지 않음.
       *
       * elmo와 상의 후 ,로 변경
       */
      categoryName: subCategoryName?.replace(/~/g, ',') || '전체',
    })
  }

  const _scrollCenter = (itemOffset: number, itemWidth: number) => {
    const screenWidth = window.screen.width > SCREEN_SIZES.md ? SCREEN_SIZES.md : window.screen.width
    const left = itemOffset - screenWidth / 2 + itemWidth / 2
    scrollDivRef.current?.scrollTo({ left, behavior: 'smooth' })
  }

  const rootCategoryWidth = rootCategories.length === 0 ? 0 : Math.floor(100 / rootCategories.length)

  if (!rootCategoryId || !rootCategories.length || !subCategories.length) {
    return null
  }

  return (
    <>
      {showAll && <ShowAllBackground />}
      <StyledContainer>
        <SliderRow paddingright={0} justify="space-around">
          {/* 사료 / 간식 / 용품 / 건강관리 / 의류 메뉴 영역 */}
          {rootCategories.map((category) => (
            <RenderItemCol
              key={category.id}
              slug="all"
              name={category.name}
              parentCategory={category}
              selected={rootCategoryId === category.id}
              applySpan={false}
              petType={petType}
              onClick={() => handleClickCategory({ toggle: false, rootCategoryId: category.id })}
              width={`${rootCategoryWidth}%`}
              isRootCategory
            />
          ))}
        </SliderRow>
        {/* 상세 카테고리 영역 */}
        <SliderRow
          wrap={showAll}
          paddingright={40}
          ref={(ref) => {
            scrollDivRef.current = ref
          }}
          justify={showAll ? 'flex-start' : 'space-between'}
        >
          {subCategoriesWithAll.map((category, index) => (
            <RenderItemCol
              key={category?.id}
              slug={category?.slug}
              name={category?.name}
              parentCategory={rootCategory}
              selected={(isAll && index === 0) || subCategoryId === category?.id}
              applySpan={showAll}
              petType={petType}
              scrollCenter={_scrollCenter}
              onClick={() =>
                handleClickCategory({ toggle: false, rootCategoryId: rootCategory.id, subCategoryId: category?.id })
              }
            />
          ))}
        </SliderRow>

        <ShowAllButtonWrapper isShowAll={showAll}>
          <ToggleButton
            className="button"
            onClick={() => {
              handleClickCategory({ toggle: !showAll })
            }}
          >
            <ImageAsset name={showAll ? 'chevron-up-grey' : 'chevron-down-grey'} size={18} />
          </ToggleButton>
        </ShowAllButtonWrapper>
      </StyledContainer>
    </>
  )
}

const RenderItemCol = ({
  all,
  slug,
  name,
  petType,
  parentCategory,
  selected,
  applySpan,
  scrollCenter,
  onClick,
  width,
  isRootCategory = false,
}: RenderItemColProps) => {
  const itemRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (selected && scrollCenter) {
      scrollCenter(itemRef.current!.offsetLeft, itemRef.current!.getBoundingClientRect().width)
    }
  }, [selected, scrollCenter])

  const url = {
    pathname: ROUTES.CATEGORY.SUB,
    query: {
      rootCategorySlug: parentCategory?.slug || slug,
      petType: petType,
      ...{ subCategorySlug: slug },
    },
  }

  const categoryName = all ? '전체' : name

  return (
    <ItemCol
      isRootCategory={isRootCategory}
      flex={isRootCategory ? '1' : applySpan ? undefined : 'none'}
      isShowAll={applySpan}
      ref={(ref) => {
        itemRef.current = ref
      }}
      style={{
        backgroundColor: isRootCategory && !selected ? GlobalColorEnum.GrayWhite : 'inherit',
        maxWidth: applySpan ? '33%' : '100%',
        width: applySpan ? '33%' : 'unset',
      }}
    >
      <LinkButton url={url} routeType="replace" shallow onClick={onClick}>
        <ItemColInside isRootCategory={isRootCategory}>
          {selected ? (
            <Text.Body3 fontWeight={FontWeightEnum.Bold} fontColor={GlobalColorEnum.Gray900}>
              {categoryName}
            </Text.Body3>
          ) : (
            <Text.Body3 fontWeight={FontWeightEnum.Regular} fontColor={GlobalColorEnum.Gray400}>
              {categoryName}
            </Text.Body3>
          )}
        </ItemColInside>
      </LinkButton>
    </ItemCol>
  )
}

const StyledContainer = styled.div`
  position: relative;
  max-width: ${LAYOUT_UNITS.BODY_MAX_WIDTH};
`

const ToggleButton = styled.div`
  position: relative;
  width: 36px;
  height: 36px;
  padding: 6px;
  border-radius: 50%;
  background-color: ${(props) => props.theme.color.grayWhite};
  cursor: pointer;
  justify-content: center;
  display: flex;
  align-items: center;
`

const SliderRow = styled(NewRow).withConfig({
  shouldForwardProp: (prop) => {
    return !['wrap'].includes(prop)
  },
})<{ paddingright: number | undefined }>`
  flex-wrap: ${({ wrap }) => (wrap ? 'wrap' : 'nowrap')};
  overflow-y: scroll;
  padding-right: ${({ paddingright }) => {
    if (paddingright === undefined) {
      return '4rem'
    }
    return paddingright
  }};
  background-color: ${(props) => props.theme.color.gray50};

  @media ${SCREEN_MEDIA_QUERY.md} {
    min-width: ${LAYOUT_UNITS.BODY_MAX_WIDTH};
  }

  @media ${SCREEN_MEDIA_QUERY.xs} {
    width: 100vw;
  }

  /* remove scrollbar */
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`

const ItemCol = styled(NewCol).withConfig({
  shouldForwardProp: (prop) => {
    return !['isRootCategory', 'isShowAll'].includes(prop)
  },
})<{ isRootCategory: boolean; isShowAll: boolean }>`
  ${({ isRootCategory, isShowAll }) =>
    !isRootCategory &&
    !isShowAll &&
    css`
      &:first-child {
        padding-left: 2rem;
      }
      &:last-child {
        padding-right: 5.2rem;
      }
    `}
`

const ItemColInside = styled.div<{ isRootCategory: boolean }>`
  padding: ${({ isRootCategory }) => (isRootCategory ? '1.3rem 1rem' : '1.5rem 0.8rem')};

  text-align: center;
`

const ShowAllBackground = styled.div`
  background-color: rgba(0, 0, 0, 0.3);
  position: fixed;
  width: 100%;
  max-width: ${LAYOUT_UNITS.BODY_MAX_WIDTH};
  height: 100%;
`

const ShowAllButtonWrapper = styled.div<{ isShowAll: boolean }>`
  padding: 0 0.8rem;
  background-color: ${({ theme, isShowAll }) => !isShowAll && theme.color.gray50};
  position: absolute;
  bottom: ${({ isShowAll }) => (isShowAll ? '-50px' : '0px')};

  right: 0;
  height: 5rem;
  display: flex;
  align-items: center;
`

export default ListCategoriesSliderV2
